import { tv } from '../../lib/tv';

/**
 * Figma Popover (87:106): bg/surface, 1px border/subtle (inside), radius/lg,
 * Shadow/MD, clipped. Header 48 (8 · 8 · 8 · 16, Title Label/Medium + Small
 * Ghost Close) · Subtle Divider · Body (space/4) · Footer (8 · 12, gap 8,
 * right-aligned; bg/subtle with a 1px border/subtle top). Widths Small 280 ·
 * Medium 320 · Large 400. Enter: fade + 4px slide from the trigger side,
 * 150ms ease-out. Shared by `PopoverContent` (Radix) and the static
 * `PopoverPanel`.
 */
export const popoverStyles = tv({
  slots: {
    root: [
      'bg-bg-surface text-text-primary z-50 flex max-w-[calc(100vw-16px)] flex-col overflow-hidden outline-none',
      'rounded-lg border border-border-subtle shadow-md',
      'max-h-(--radix-popover-content-available-height) origin-(--radix-popover-content-transform-origin)',
      'duration-150 ease-out',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
      'data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
    ],
    header: 'flex shrink-0 items-center py-2 pr-2 pl-4',
    title: 'text-label-md text-text-primary min-w-0 flex-1',
    divider: 'bg-border-subtle',
    body: 'flex min-h-0 flex-col overflow-y-auto p-4',
    footer:
      'bg-bg-subtle flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-3 py-2',
  },
  variants: {
    size: {
      sm: { root: 'w-70' },
      md: { root: 'w-80' },
      lg: { root: 'w-100' },
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Figma Hover Card (87:179): the Popover surface (bg/surface, 1px
 * border/subtle, radius/lg, Shadow/MD). User: 300 wide, padding space/4, a
 * column with gap space/3. Reel: 320 wide, padding 12, a row with gap 12.
 */
export const hoverCardStyles = tv({
  slots: {
    root: [
      'bg-bg-surface text-text-primary z-50 flex max-w-[calc(100vw-16px)] overflow-hidden outline-none',
      'rounded-lg border border-border-subtle shadow-md',
      'origin-(--radix-hover-card-content-transform-origin) duration-150 ease-out',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
    ],
  },
  variants: {
    type: {
      user: { root: 'w-75 flex-col gap-3 p-4' },
      reel: { root: 'w-80 flex-row gap-3 p-3' },
    },
  },
  defaultVariants: { type: 'user' },
});

/**
 * The Modal / Drawer backdrop: bg/overlay (Ink 48%), fading with its dialog
 * (200ms in, 150ms out).
 */
export const overlayBackdrop = [
  'bg-bg-overlay fixed inset-0 z-50 ease-out',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-150',
].join(' ');

/** Centred in the viewport with 16px to spare; fade + scale 0.96 → 1, 200ms in / 150ms out. */
export const modalPosition = [
  'fixed inset-0 z-50 m-auto h-fit max-h-[calc(100dvh-32px)]',
  'ease-out data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-96 data-[state=open]:duration-200',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-96 data-[state=closed]:duration-150',
].join(' ');

/**
 * Figma Modal (76:121): bg/surface, radius/xl, Shadow/Overlay, clipped, no
 * stroke. Header 20 · 16 · 16 · 24, gap 12 (Titles: 4px top, gap 4, Title
 * Heading/H4, Description Body/Medium text/secondary; Small Ghost Close) ·
 * Body 0 · 24 · 24 · 24 · Footer 16 · 24 (bg/subtle, 1px border/subtle top,
 * gap 8: a fill "Left" area for the Footer checkbox, then Secondary and
 * Primary Medium buttons). Small 400 · Medium 560 · Large 720.
 */
export const modalStyles = tv({
  slots: {
    root: [
      'bg-bg-surface text-text-primary flex max-w-[calc(100vw-32px)] flex-col overflow-hidden outline-none',
      'rounded-xl shadow-overlay',
    ],
    header: 'flex shrink-0 items-start gap-3 pt-5 pr-4 pb-4 pl-6',
    titles: 'flex min-w-0 flex-1 flex-col gap-1 pt-1',
    title: 'text-heading-h4 text-text-primary',
    description: 'text-body-md text-text-secondary',
    body: 'min-h-0 flex-1 overflow-y-auto px-6 pb-6',
    footer:
      'bg-bg-subtle flex shrink-0 items-center gap-2 border-t border-border-subtle px-6 py-4',
    footerStart: 'flex min-h-5 min-w-0 flex-1 items-center',
  },
  variants: {
    size: {
      sm: { root: 'w-100' },
      md: { root: 'w-140' },
      lg: { root: 'w-180' },
      // Not in Figma: a workspace in a modal (review a draft beside its comments). Fixed height,
      // so the body can split into columns that scroll on their own.
      xl: { root: 'h-[min(90dvh,880px)] w-280' },
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Figma Confirm Dialog (76:177): the Small modal surface (400, bg/surface,
 * radius/xl, Shadow/Overlay), padding space/6, gap space/4. A 44px icon
 * circle (22px glyph: Danger status/danger bg / fg, Neutral brand/subtle +
 * icon/brand), Text (gap 6: Title Heading/H4, Body Body/Medium
 * text/secondary) and Actions (8px top, gap 8, right-aligned: Ghost Cancel +
 * Danger or Primary confirm, Medium).
 */
export const confirmDialogStyles = tv({
  slots: {
    root: [
      'bg-bg-surface text-text-primary flex w-100 max-w-[calc(100vw-32px)] flex-col gap-4 overflow-hidden p-6 outline-none',
      'rounded-xl shadow-overlay',
    ],
    icon: 'flex size-11 shrink-0 items-center justify-center rounded-full [&>svg]:size-[22px]',
    text: 'flex flex-col gap-1.5',
    title: 'text-heading-h4 text-text-primary',
    description: 'text-body-md text-text-secondary',
    actions: 'flex flex-wrap items-center justify-end gap-2 pt-2',
  },
  variants: {
    tone: {
      danger: { icon: 'bg-status-danger-bg text-status-danger-fg' },
      neutral: { icon: 'bg-brand-subtle text-icon-brand' },
    },
  },
  defaultVariants: { tone: 'danger' },
});

/**
 * Figma Drawer (87:9586): full-height side sheet, bg/surface, Shadow/Overlay,
 * a 1px border/subtle on the inner edge (left for Right, right for Left),
 * no radius. Header 20 · 16 · 16 · 24 (Titles: 4px top, gap 4, Title
 * Heading/H4, Description Body/Small text/secondary; Small Ghost Close) ·
 * Subtle Divider · Body space/6, fills and scrolls · Footer 16 · 24, gap 8,
 * bg/surface with a 1px border/subtle top, actions right-aligned.
 * Default 400 · Wide 560 (the border is inside the width).
 */
export const drawerStyles = tv({
  slots: {
    root: 'bg-bg-surface text-text-primary flex max-w-full flex-col overflow-hidden border-border-subtle shadow-overlay outline-none',
    header: 'flex shrink-0 items-start gap-3 pt-5 pr-4 pb-4 pl-6',
    titles: 'flex min-w-0 flex-1 flex-col gap-1 pt-1',
    title: 'text-heading-h4 text-text-primary',
    description: 'text-body-sm text-text-secondary',
    divider: 'bg-border-subtle',
    body: 'flex min-h-0 flex-1 flex-col overflow-y-auto p-6',
    footer:
      'bg-bg-surface flex shrink-0 items-center gap-2 border-t border-border-subtle px-6 py-4',
    footerStart: 'flex min-w-0 flex-1 items-center',
  },
  variants: {
    side: {
      right: { root: 'border-l' },
      left: { root: 'border-r' },
    },
    size: {
      default: { root: 'w-100' },
      wide: { root: 'w-140' },
    },
  },
  defaultVariants: { side: 'right', size: 'default' },
});

/** Pinned to its edge, full height; slide 24px + fade, 240ms ease-out in, 180ms out. */
export const drawerPosition = tv({
  base: [
    'fixed inset-y-0 z-50 h-dvh ease-out',
    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-240',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-180',
  ],
  variants: {
    side: {
      right:
        'right-0 data-[state=open]:slide-in-from-right-6 data-[state=closed]:slide-out-to-right-6',
      left: 'left-0 data-[state=open]:slide-in-from-left-6 data-[state=closed]:slide-out-to-left-6',
    },
  },
  defaultVariants: { side: 'right' },
});
